import { UniverseImagePipe } from '@/domain/universes/pipes/universe-image.pipe';
import type { UniverseId } from '@/domain/universes/universe.interface';
import { UniverseService } from '@/domain/universes/universe.service';
import type { ClassificationNodeId, ClassificationNodePatrimonialData } from '@/interfaces/Classification.interface';
import { ClassificationsProviderV2 } from '@/providers/classification/classifications-v2.provider';
import { UiAvatarComponent } from '@/ui/avatar/ui-avatar';
import { UiButtonComponent } from '@/ui/button/ui-button';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiSectionActionsDirective } from '@/ui/section/section-actions/ui-section-actions';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { UiTableModule } from '@/ui/table/ui-table.module';
import { ApplyPureFunctionPipe } from '@/utils/angular/pipes/apply-pure-function.pipe';
import { ReferenceListLabelPipe } from '@/utils/reference-lists/pipes/reference-list-label.pipe';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { concatReducer } from '@/utils/toolkit/array/concat.utils';
import { significantDigitConversion } from '@/utils/toolkit/numbers/significant-digits.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, type OnInit, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { type Observable, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { sseGreyLight } from '../../constants/color';
import { PeriodIdentifier } from '../../constants/period.constant';
import { AdvizeoTooltipDirective } from '../../directives/advizeo-tooltip/advizeo-tooltip.directive';
import type { Period } from '../../interfaces/Building.interface';
import type { RecordedEstimatedData, UniverseDataMap } from '../../interfaces/GenericData.interface';
import { TimeseriesIndicatorCode } from '../../interfaces/Timeseries.interface';
import type { UnitCode, UnitFormatter } from '../../interfaces/Unit.interface';
import type { BalanceTableConfig } from '../../interfaces/Widget.interface';
import { SharedUiColoredTextColorDirective } from '../../libs/shared/ui/theming/lib/colors/shared-ui-color.directives';
import { LocalNumberCurrencyPipe, LocalNumberPipe, SignificantDigitPipe } from '../../pipes/localnumber.pipe';
import { TranslatePeriodPipe } from '../../pipes/translate-period.pipe';
import type { VatModes } from '../../providers/budgets/budgets.constants';
import { CsvExportProvider } from '../../providers/csv-export/csv-export';
import { DashboardingProvider } from '../../providers/dashboarding.provider';
import { DataProvider } from '../../providers/data/data.provider';
import { UnitProvider } from '../../providers/unit/unit.provider';
import { WidgetProvider } from '../../providers/widget.provider';
import { Page, RoutingService } from '../../services/routing/routing.service';
import { HelpMeComponent } from '../help-me/help-me';
import { LoadingAndErrorComponent } from '../loading-and-error/loading-and-error';

export interface TableDatasource {
    isTotal: boolean;
    universeName: UniverseId | null;
    consumptionIndicator: TimeseriesIndicatorCode | null;
    energyDataCurrentPeriod: number | null;
    energyDataPreviousPeriod: number | null;
    budgetDataCurrentPeriod: RecordedEstimatedData | null;
    budgetDataPreviousPeriod: RecordedEstimatedData | null;
    unitPriceCurrentPeriod: number | null;
    unitPricePreviousPeriod: number | null;
    unitPriceEvolutionRatio: number | null;
    budgetGapCurrentPeriod: number | null;
    budgetGapRatio: number | null;
    pricePerSurfaceUnit: number | null;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./budget-balance-table.scss'],
    selector: 'budget-balance-table',
    templateUrl: 'budget-balance-table.html',
    imports: [
        UiSectionComponent,
        UiSectionActionsDirective,
        UiIconButtonComponent,
        AdvizeoTooltipDirective,
        UiButtonComponent,
        UiSectionContentComponent,
        UiTableModule,
        UiAvatarComponent,
        HelpMeComponent,
        SharedUiColoredTextColorDirective,
        LoadingAndErrorComponent,
        AsyncPipe,
        TranslatePipe,
        LocalNumberPipe,
        LocalNumberCurrencyPipe,
        SignificantDigitPipe,
        UniverseImagePipe,
        TranslatePeriodPipe,
        ReferenceListLabelPipe,
        ApplyPureFunctionPipe
    ]
})
export class BudgetBalanceTableComponent implements OnInit {
    private _translate = inject(TranslateService);
    private _routingService = inject(RoutingService);
    private _csvExportProvider = inject(CsvExportProvider);
    private _classificationsProvider = inject(ClassificationsProviderV2);
    private _referenceListsService = inject(ReferenceListsService);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _dataProvider = inject(DataProvider);
    private _unitProvider = inject(UnitProvider);
    private _universeService = inject(UniverseService);
    private _widgetsProvider = inject(WidgetProvider);

    headers$!: Observable<string[]>;
    period$!: Observable<PeriodIdentifier>;

    surfaceUnit: string = 'm²';
    dataSource: TableDatasource[] = [];

    lastTwelveMonths = PeriodIdentifier.LAST_TWELVE_MONTHS;
    sseGreyLight = sseGreyLight;

    vatModeForTranslations!: VatModes;

    ignoreEstimated?: boolean;

    isLoading: boolean = true;
    loadingError: boolean = false;

    targetUnits: Partial<Record<TimeseriesIndicatorCode, UnitCode>> = {};

    @Input() config!: BalanceTableConfig;
    currencyBaseUnitCode!: UnitCode;

    formatters: {
        targetUnitsFormatters: Partial<Record<TimeseriesIndicatorCode, UnitFormatter>>;
        unitPriceFormatters: Partial<Record<TimeseriesIndicatorCode, UnitFormatter>>;
        budgetGapFormatter: UnitFormatter;
        currencyBySurfaceFormatter: UnitFormatter;
    } = {
        targetUnitsFormatters: {},
        unitPriceFormatters: {},
        budgetGapFormatter: () => '',
        currencyBySurfaceFormatter: () => ''
    };

    // be careful, for this page to work, these indicators should have no overlaping universes
    consumptionTimeseriesIndicators = [TimeseriesIndicatorCode.WATER_CONSUMPTION, TimeseriesIndicatorCode.ENERGY_CONSUMPTION];

    ngOnInit() {
        this.headers$ = this._dashboardingProvider.isEstimatedIgnoredOnWidgets$().pipe(
            map(ignoreEstimated => {
                if (ignoreEstimated) {
                    return ['type', 'universe', 'actualConsumption', 'invoicedAmount', 'unitPrice', 'budgetGap', 'pricePerSurfaceUnit'];
                } else {
                    return [
                        'type',
                        'universe',
                        'actualConsumption',
                        'invoicedAmount',
                        'estimatedAmount',
                        'unitPrice',
                        'budgetGap',
                        'pricePerSurfaceUnit'
                    ];
                }
            })
        );

        this.period$ = this._dashboardingProvider.getSelectedPeriodIdentifier$().pipe(filter(isNotNullish));

        this.currencyBaseUnitCode = this._unitProvider.getDeliveryPointTimeseriesUnitCode(this.config.timeseriesIndicator);
        this.vatModeForTranslations = this._widgetsProvider.getVatModeFromTimeseriesIndicator(this.config.timeseriesIndicator);
        combineLatest([
            this._dashboardingProvider.getMeaningfulNodeIdAndSelectedPeriod$(),
            this._dashboardingProvider.isEstimatedIgnoredOnWidgets$()
        ])
            .pipe(untilDestroyed(this))
            .subscribe(([[classificationNodeId, period], ignoreEstimated]) => {
                this.ignoreEstimated = ignoreEstimated;
                this.fetchData(period, ignoreEstimated, classificationNodeId);
            });
    }

    private fetchData(period: Period, ignoreEstimated: boolean, classificationNodeId: ClassificationNodeId) {
        const normalizedPeriod = this._dashboardingProvider.normalizePeriodTo12FullMonths(period);

        this.isLoading = true;
        this.loadingError = false;

        Promise.all([
            this._dataProvider
                .getUniverseAggregatedDeliveryPointTimeseriesPoint$(
                    {
                        classificationNodeId,
                        indicatorCodes: [this.config.timeseriesIndicator]
                    },
                    normalizedPeriod.start.clone().subtract(1, 'year'),
                    normalizedPeriod.end,
                    ignoreEstimated
                )
                .toPromise(),
            this._dataProvider
                .getUniverseAggregatedDeliveryPointTimeseriesPoint$(
                    {
                        classificationNodeId,
                        indicatorCodes: this.consumptionTimeseriesIndicators,
                        metrics: ['invoice']
                    },
                    normalizedPeriod.start.clone().subtract(1, 'year'),
                    normalizedPeriod.end,
                    ignoreEstimated
                )
                .toPromise(),
            firstValueFrom(this._classificationsProvider.getClassificationNodePatrimonialData$(classificationNodeId))
        ])
            .then(([budgetData, invoices, patrimonialData]) => {
                let universeIdSet: Set<UniverseId>;
                let budgetDataByUniverseCurrentPeriod: UniverseDataMap<RecordedEstimatedData>;
                let budgetDataByUniversePreviousPeriod: UniverseDataMap<RecordedEstimatedData>;

                if (!budgetData || !budgetData.length) {
                    universeIdSet = new Set<UniverseId>();
                    budgetDataByUniverseCurrentPeriod = new Map();
                    budgetDataByUniversePreviousPeriod = new Map();
                } else {
                    universeIdSet = new Set<UniverseId>(budgetData.map(data => data.universeId));
                    budgetDataByUniverseCurrentPeriod = this._dataProvider.mapTimeseriesDataToUniverseData(
                        budgetData.filter(budgetData => moment(budgetData.date).isSameOrAfter(normalizedPeriod.start, 'day'))
                    );
                    budgetDataByUniversePreviousPeriod = this._dataProvider.mapTimeseriesDataToUniverseData(
                        budgetData.filter(budgetData => moment(budgetData.date).isBefore(normalizedPeriod.start, 'day'))
                    );
                }

                const indicatorCodeByUniverse: Map<UniverseId, TimeseriesIndicatorCode> = new Map();
                const energyBillByUniverseCurrentPeriod: Map<UniverseId, number> = new Map();
                const energyBillByUniversePreviousPeriod: Map<UniverseId, number> = new Map();

                const currentPeriodStart = normalizedPeriod.start;
                const currentPeriodEnd = normalizedPeriod.end;

                const previousPeriodStart = normalizedPeriod.start.clone().subtract(1, 'year');
                const previousPeriodEnd = normalizedPeriod.end.clone().subtract(1, 'year');

                if (invoices && invoices.length) {
                    invoices.forEach(invoice => {
                        const universe = invoice.universeId;
                        const invoiceDate = moment(invoice.date);
                        if (invoiceDate.isSameOrAfter(currentPeriodStart) && invoiceDate.isBefore(currentPeriodEnd)) {
                            energyBillByUniverseCurrentPeriod.set(
                                universe,
                                invoice.value! + (energyBillByUniverseCurrentPeriod.get(universe) || 0)
                            );
                        } else if (invoiceDate.isSameOrAfter(previousPeriodStart) && invoiceDate.isBefore(previousPeriodEnd)) {
                            energyBillByUniversePreviousPeriod.set(
                                universe,
                                invoice.value! + (energyBillByUniversePreviousPeriod.get(universe) || 0)
                            );
                        }

                        if (invoice.value) {
                            indicatorCodeByUniverse.set(universe, invoice.indicatorCode);
                        }
                    });
                }

                if (budgetDataByUniverseCurrentPeriod.size || budgetDataByUniversePreviousPeriod.size) {
                    this.buildTable(
                        universeIdSet,
                        indicatorCodeByUniverse,
                        budgetDataByUniverseCurrentPeriod,
                        budgetDataByUniversePreviousPeriod,
                        energyBillByUniverseCurrentPeriod,
                        energyBillByUniversePreviousPeriod,
                        patrimonialData
                    );
                } else {
                    this.dataSource = [];
                }
                this.isLoading = false;
            })
            .catch(error => {
                this.isLoading = false;
                this.loadingError = true;
                throw error;
            });
    }

    private buildTable(
        universeIdSet: Set<UniverseId>,
        indicatorCodeByUniverse: Map<UniverseId, TimeseriesIndicatorCode>,
        budgetDataByUniverseCurrentPeriod: Map<UniverseId, RecordedEstimatedData>,
        budgetDataByUniversePreviousPeriod: Map<UniverseId, RecordedEstimatedData>,
        energyBillByUniverseCurrentPeriod: Map<UniverseId, number>,
        energyBillByUniversePreviousPeriod: Map<UniverseId, number>,
        patrimonialData: ClassificationNodePatrimonialData
    ) {
        const totals: TableDatasource = {
            isTotal: true,
            universeName: null,
            consumptionIndicator: null,
            energyDataCurrentPeriod: null,
            energyDataPreviousPeriod: null,
            budgetDataCurrentPeriod: null,
            budgetDataPreviousPeriod: null,
            unitPriceCurrentPeriod: null,
            unitPricePreviousPeriod: null,
            unitPriceEvolutionRatio: null,
            budgetGapCurrentPeriod: 0,
            budgetGapRatio: null,
            pricePerSurfaceUnit: null
        };

        const dataByUniverse: TableDatasource[] = Array.from(universeIdSet).map<TableDatasource>(universeName => {
            const budgetDataCurrentPeriod = budgetDataByUniverseCurrentPeriod.has(universeName)
                ? budgetDataByUniverseCurrentPeriod.get(universeName)!
                : this._dataProvider.getEmptyUniverseData(universeName);
            const budgetDataPreviousPeriod = budgetDataByUniversePreviousPeriod.has(universeName)
                ? budgetDataByUniversePreviousPeriod.get(universeName)!
                : this._dataProvider.getEmptyUniverseData(universeName);

            const budgetGapCurrentPeriod =
                (budgetDataCurrentPeriod.recorded ?? 0) +
                (budgetDataCurrentPeriod.estimated ?? 0) -
                (budgetDataCurrentPeriod.reference ?? 0);
            const budgetGapRatio = budgetDataCurrentPeriod.reference
                ? ((budgetDataCurrentPeriod.recorded ?? 0) + (budgetDataCurrentPeriod.estimated ?? 0)) / budgetDataCurrentPeriod.reference -
                  1
                : null;

            const energyDataCurrentPeriod = energyBillByUniverseCurrentPeriod.has(universeName)
                ? energyBillByUniverseCurrentPeriod.get(universeName)!
                : 0;
            const energyDataPreviousPeriod = energyBillByUniversePreviousPeriod.has(universeName)
                ? energyBillByUniversePreviousPeriod.get(universeName)!
                : 0;

            const unitPriceCurrentPeriod = energyDataCurrentPeriod
                ? (budgetDataCurrentPeriod.recorded ?? 0) / energyDataCurrentPeriod
                : null;
            const unitPricePreviousPeriod = energyDataPreviousPeriod
                ? (budgetDataPreviousPeriod.recorded ?? 0) / energyDataPreviousPeriod
                : null;

            totals.budgetDataCurrentPeriod = this._dataProvider.sumRecordedEstimatedData([
                budgetDataCurrentPeriod,
                totals.budgetDataCurrentPeriod
            ]);
            totals.budgetDataPreviousPeriod = this._dataProvider.sumRecordedEstimatedData([
                budgetDataPreviousPeriod,
                totals.budgetDataPreviousPeriod
            ]);
            totals.budgetGapCurrentPeriod = (totals.budgetGapCurrentPeriod ?? 0) + budgetGapCurrentPeriod;

            return {
                isTotal: false,
                universeName,
                consumptionIndicator: indicatorCodeByUniverse.get(universeName)!,
                energyDataCurrentPeriod,
                energyDataPreviousPeriod,
                budgetDataCurrentPeriod,
                budgetDataPreviousPeriod,
                unitPriceCurrentPeriod,
                unitPricePreviousPeriod,
                unitPriceEvolutionRatio: unitPricePreviousPeriod ? (unitPriceCurrentPeriod ?? 0) / unitPricePreviousPeriod - 1 : null,
                budgetGapCurrentPeriod,
                budgetGapRatio,
                pricePerSurfaceUnit: patrimonialData.usefulSurface
                    ? ((budgetDataCurrentPeriod.recorded ?? 0) + (budgetDataCurrentPeriod.estimated ?? 0)) / patrimonialData.usefulSurface
                    : null
            };
        });

        this.initializeTargetUnitsAndFormatters(dataByUniverse);

        const totalData = this._dataProvider.sumRecordedEstimatedData(Array.from(budgetDataByUniverseCurrentPeriod.values()));
        totals.budgetGapRatio = totalData.reference
            ? ((totalData.recorded ?? 0) + (totalData.estimated ?? 0)) / totalData.reference - 1
            : null;
        totals.pricePerSurfaceUnit = patrimonialData.usefulSurface
            ? ((totals.budgetDataCurrentPeriod?.recorded ?? 0) + (totals.budgetDataCurrentPeriod?.estimated ?? 0)) /
              patrimonialData.usefulSurface
            : null;

        dataByUniverse.sort((line1, line2) => {
            return this.getUniverse(line1.universeName).localeCompare(this.getUniverse(line2.universeName));
        });

        dataByUniverse.push(totals);
        this.dataSource = dataByUniverse;
    }

    private initializeTargetUnitsAndFormatters(dataByUniverse: TableDatasource[]) {
        // target units
        this.targetUnits[this.config.timeseriesIndicator] = this._unitProvider.getBestConversion(
            dataByUniverse
                .map(dataLine => [dataLine.budgetDataCurrentPeriod?.recorded ?? 0, dataLine.budgetDataCurrentPeriod?.estimated ?? 0])
                .reduce(concatReducer),
            this._unitProvider.getDeliveryPointTimeseriesUnitCode(this.config.timeseriesIndicator)
        );

        this.consumptionTimeseriesIndicators.forEach(consumptionIndicator => {
            this.targetUnits[consumptionIndicator] = this._unitProvider.getBestConversion(
                dataByUniverse
                    .filter(dataLine => dataLine.consumptionIndicator === consumptionIndicator)
                    .map(dataLine => dataLine.energyDataCurrentPeriod ?? 0),
                this._unitProvider.getDeliveryPointTimeseriesUnitCode(consumptionIndicator)
            );
        });

        // target unit formatters
        (Object.keys(this.targetUnits) as TimeseriesIndicatorCode[]).forEach(indicatorCode => {
            this.formatters.targetUnitsFormatters[indicatorCode] = this._unitProvider.formatterFactory(
                this._unitProvider.getDeliveryPointTimeseriesUnitCode(indicatorCode),
                this.targetUnits[indicatorCode]!
            );
        });

        // unit price formatters
        this.consumptionTimeseriesIndicators.forEach(indicatorCode => {
            this.formatters.unitPriceFormatters[indicatorCode] = this._unitProvider.ratioFormatterFactory(
                {
                    sourceUnitCode: this.currencyBaseUnitCode,
                    targetUnitCode: this.currencyBaseUnitCode
                },
                {
                    sourceUnitCode: this._unitProvider.getDeliveryPointTimeseriesUnitCode(indicatorCode),
                    targetUnitCode: this.targetUnits[indicatorCode]!
                },
                {
                    minimumDecimals: 2,
                    maximumDecimals: 2,
                    significantDigits: -1
                }
            );
        });

        // budget gap formatter
        this.formatters.budgetGapFormatter = this._unitProvider.formatterFactory(
            this.currencyBaseUnitCode,
            this.targetUnits[this.config.timeseriesIndicator]!,
            {
                displayNumberSign: true
            }
        );
    }

    goToBillsByBuilding() {
        const selectedLegacyDashboard = this._dashboardingProvider.getSelectedLegacyDashboard();
        if (selectedLegacyDashboard) {
            this._routingService.goTo(Page.BILLS_BY_BUILDING, {
                indicatorCode: this.config.timeseriesIndicator,
                dashboardRight: selectedLegacyDashboard.dashboardRight
            });
        }
    }

    exportDataAsCsv() {
        this._csvExportProvider.downloadCsvFromDataSource(this.dataSource, [
            {
                header: this._translate.instant('WIDGET.BUDGET_BALANCE_TABLE.UNIVERSE'),
                value: universeData => {
                    if (universeData.isTotal) {
                        return this._translate.instant('STUFF.TOTAL');
                    } else {
                        return this._translate.instant(this.getUniverse(universeData.universeName));
                    }
                }
            },
            {
                header: this._translate.instant('WIDGET.BUDGET_BALANCE_TABLE.ACTUAL_CONSUMPTION'),
                value: universeData => {
                    if (!universeData.isTotal) {
                        return significantDigitConversion(universeData.energyDataCurrentPeriod);
                    }
                }
            },
            {
                header: this._translate.instant('WIDGET.BUDGET_BALANCE_TABLE.ACTUAL_CONSUMPTION_UNIT'),
                value: universeData => {
                    if (!universeData.isTotal) {
                        const universe = universeData.universeName ? this._universeService.getUniverseR(universeData.universeName) : null;
                        return universe ? universe.baseUnitCode : '';
                    }
                }
            },
            {
                header: this._translate.instant('WIDGET.BUDGET_BALANCE_TABLE.INVOICED_AMOUNT'),
                value: universeData =>
                    universeData.budgetDataCurrentPeriod ? significantDigitConversion(universeData.budgetDataCurrentPeriod.recorded) : null
            },
            ...(this.ignoreEstimated
                ? []
                : [
                      {
                          header: this._translate.instant('WIDGET.BUDGET_BALANCE_TABLE.ESTIMATED_AMOUNT'),
                          value: (universeData: TableDatasource) =>
                              universeData.budgetDataCurrentPeriod
                                  ? significantDigitConversion(universeData.budgetDataCurrentPeriod.estimated)
                                  : null
                      }
                  ]),
            {
                header: this._translate.instant('WIDGET.BUDGET_BALANCE_TABLE.UNIT_PRICE'),
                value: universeData => {
                    if (!universeData.isTotal) {
                        return significantDigitConversion(universeData.unitPriceCurrentPeriod);
                    }
                }
            },
            {
                header: this._translate.instant('WIDGET.BUDGET_BALANCE_TABLE.BUDGET_GAP'),
                value: universeData => significantDigitConversion(universeData.budgetGapCurrentPeriod)
            },
            {
                header: this._translate.instant('WIDGET.BUDGET_BALANCE_TABLE.PRICE_PER_SURFACE_UNIT', {
                    currency: this._referenceListsService.getReferenceListItemLabelR(ReferenceListName.UNIT, this.currencyBaseUnitCode),
                    surfaceUnit: this.surfaceUnit
                }),
                value: universeData => significantDigitConversion(universeData.pricePerSurfaceUnit)
            }
        ]);
    }

    private getUniverse(universeName: UniverseId | null): string {
        return universeName ? this._referenceListsService.getReferenceListItemLabelR(ReferenceListName.UNIVERSE, universeName) : '';
    }
}
